import React, { useCallback, useContext } from 'react';
import styled, { css } from 'styled-components';
import { SelectField } from '@ubeya/shared-web/components';
import useAddNewSelector from '@ubeya/shared-web/hooks/useAddNewSelector';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import SectionsModal from 'scheduling/components/SectionsModal/SectionsModal';

const Label = styled.span`
  color: ${({ theme, $color }) => $color || theme.colors.black};
`;

const Wrapper = styled.div.attrs(() => ({ className: 'uniform-wrapper' }))`
  ${({ $hideBorder }) =>
    $hideBorder &&
    css`
      .select {
        border: none;
        box-shadow: none;
      }
    `}
`;

const LabelsSelectField = ({
  name = 'labelId',
  hideBorder = false,
  title,
  onChangeExternal,
  hideTitle = false,
  ...restSelectProps
}) => {
  const { t } = useContext(ConfigContext);

  const { labelOptions: defaultLabelOptions, mappedLabels } = useLabels();

  const {
    isOpen: labelsModal,
    setIsOpen: setLabelsModal,
    options: labelOptions,
    onChange: onChangeLabel
  } = useAddNewSelector({
    options: defaultLabelOptions,
    addText: 'addNewLabel'
  });

  const onChangeInternal = useCallback(
    (labelId) => {
      onChangeLabel(labelId);
      onChangeExternal?.({ labelId });
    },
    [onChangeExternal, onChangeLabel]
  );

  const onAdd = useCallback(
    ({ added }) => {
      added?.[0]?.id &&
        onChangeExternal({
          labelId: added[0].id,
          labelStartTimeProp: added[0].startTime,
          labelEndTimeProp: added[0].endTime
        });
    },
    [onChangeExternal]
  );

  return (
    <Wrapper $hideBorder={hideBorder}>
      <SelectField
        name={name}
        title={hideTitle ? '' : title ?? t('label')}
        options={labelOptions}
        onChange={onChangeInternal}
        formatValue={({ value, displayValue }) => (
          <Label $color={mappedLabels[value]?.color}>{displayValue || t('searchPlaceholder')}</Label>
        )}
        formatOption={({ value, label }) => ({
          value,
          label: <Label $color={value && mappedLabels[value]?.color}>{label}</Label>
        })}
        usePortal
        isMulti={false}
        {...restSelectProps}
      />

      {labelsModal && (
        <SectionsModal selectedSection={{ id: labelsModal }} onClose={() => setLabelsModal()} onConfirm={onAdd} />
      )}
    </Wrapper>
  );
};

export default LabelsSelectField;
